import { Autocomplete, ListSubheader, Paper, Tooltip, Typography } from '@mui/material';
import { Check } from '@mui/icons-material';
import checked_checkbox from '../../assets/images/checked_checkbox.svg';
import unchecked_checkbox from '../../assets/images/unchecked_checkbox.svg';
import magnifying_glass from '../../assets/images/magnifying_glass.svg';
import { useEffect, useMemo, useRef, useState } from 'react';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import { UpgradeModal } from '../../components/UpgradeModal/UpgradeModal';
import { LEAGUE_SPORT_MAP } from './leagueToSportMapping';
import { SPORTS } from '../../utils/constants';
import './Screener.css';

// Same as other tools: Popular first, then by sport
const LEAGUE_SPORT_CUSTOM_MAP = {
	'NBA': 'popular',
	'NHL': 'popular',
	'NFL': 'popular',
	'MLB': 'popular',
	'NCAAB': 'popular',
	'NCAAF': 'popular',
	'ATP': 'tennis',
	'ATP Challenger': 'tennis',
	'WTA': 'tennis',
	'Belarus - Premier League': 'soccer',
	'Spain - La Liga': 'soccer',
	'Spain - La Liga 2': 'soccer',
	'CFL': 'football',
	'CS2': 'esports',
	'Dota 2': 'esports',
	'League of Legends': 'esports',
	'Rocket League': 'esports',
	'Valorant': 'esports',
	'NCAAH': 'hockey',
	'PGA': 'golf',
	'UFC': 'mma',
	'International - Tours': 'tennis',
	'International - Tours Women': 'tennis',
};

const getSportDisplayName = (sportKey) => {
	if (sportKey === 'popular') return 'Popular';
	const sport = SPORTS.find((s) => s.value === sportKey);
	return sport ? sport.label.charAt(0).toUpperCase() + sport.label.slice(1) : sportKey.charAt(0).toUpperCase() + sportKey.slice(1);
};

const sportGroupOrder = (a, b) => {
	if (a === 'popular') return -1;
	if (b === 'popular') return 1;
	if (a === 'other') return 1;
	if (b === 'other') return -1;
	return a.localeCompare(b);
};

const StyledPopper = styled(Popper)({
	zIndex: 1300, // make sure it's above everything else if needed
});

const CustomPopper = (props) => {
	return <StyledPopper {...props} placement="bottom-start" modifiers={[]} />;
};

const autocompleteStyling = {
	minWidth: 150,
	width: 160,
	// width: 'max-content',
	'&.MuiAutocomplete-root': {
		backgroundColor: '#17191d',
		color: '#eff1f5 !important',
		outline: 'none',
		boxShadow: 'none',
	},
	'&.MuiAutocomplete-root:focus-within': {
		outline: 'none',
		boxShadow: 'none',
	},
	'&.MuiInputBase-input': { color: '#eff1f5' },
	borderRadius: '0.5rem',
	'& .MuiAutocomplete-popupIndicator': {
		color: '#eff1f5',
	},
	'&.MuiChip-root': {
		color: '#eff1f5',
	},
	'& .MuiAutocomplete-tag': { color: 'red' },
	'& .MuiChip-root': { color: '#eff1f5', backgroundColor: '#22262e' },
	'& .MuiAutocomplete-clearIndicator': {
		color: '#eff1f5',
	},
	'& .MuiChip-deleteIcon': {
		color: '#eff1f5',
	},
	'& .MuiInputBase-input': {
		color: '#eff1f5', // Default input text color
	},
	'& .MuiInputBase-input:focus': {
		color: '#eff1f5', // Change color when focused
	},
	'& .MuiOutlinedInput-root': {
		border: '2px solid #363844',
		borderRadius: '8px',
		'&:hover': {
			borderColor: '#1e355e', // On hover
		},
		'&.Mui-focused': {
			borderColor: '#1e355e', // On focus
		},
	},
};

const CustomPaper = (props) => {
	return <Paper {...props} style={{ backgroundColor: '#17191d', borderRadius: 10, padding: 8, overflow: 'hidden' }} />;
};

export const ScreenerAutocomplete = (props) => {
	const { options, value, whichFiltersOpen, setWhichFiltersOpen, filters, setFilters, filterName, filterTitle, filterPlaceholder, tooltip, refreshCounter, setRefreshCounter } = props;

	const inputRef = useRef(null);
	const wrapperRef = useRef(null);
	const [isOpen, setIsOpen] = useState(false);
	// console.log('isOpen: ', isOpen, filterName);

	const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
	const [upgradeModalMessage, setUpgradeModalMessage] = useState('');

	const user = JSON.parse(window.localStorage.getItem('userdb'));
	const isMasterOrOracle =
		(user?.stripe_plan?.nickname?.includes('Visionary') &&
			user?.stripe_plan.active &&
			user?.stripe_subscription !== null &&
			user?.stripe_status !== 'deleted' &&
			user.stripe_status !== 'past_due') ||
		(user?.stripe_plan?.nickname?.includes('Oracle') &&
			user?.stripe_plan.active &&
			user?.stripe_subscription !== null &&
			user?.stripe_status !== 'deleted' &&
			user.stripe_status !== 'past_due');

	const isSingleSelect = filterName === 'leagues';

	// For leagues: group by sport with Popular first (same structure as other tools)
	const leagueOptionsWithGroup = useMemo(() => {
		if (filterName !== 'leagues' || !options?.length) return options ?? [];
		const withGroup = options.map((o) => ({
			...o,
			sportGroup: LEAGUE_SPORT_CUSTOM_MAP[o.value] || LEAGUE_SPORT_MAP[o.value] || 'other',
		}));
		const order = Object.keys(
			withGroup.reduce((acc, o) => {
				acc[o.sportGroup] = true;
				return acc;
			}, {})
		).sort(sportGroupOrder);
		return withGroup.sort((a, b) => order.indexOf(a.sportGroup) - order.indexOf(b.sportGroup));
	}, [filterName, options]);

	const displayOptions = filterName === 'leagues' ? leagueOptionsWithGroup : (options ?? []);

	// For single-select (e.g. League), value is a string; normalize to option object or null for MUI Autocomplete
	const normalizedValue = isSingleSelect
		? (displayOptions.find((o) => o.value === value || o.label === value) || null)
		: value || [];

	useEffect(() => {
		if (isOpen && inputRef.current && typeof inputRef.current.focus === 'function') {
			inputRef.current.focus();
		}
	}, [isOpen]);

	useEffect(() => {
		function handleClickOutside(event) {
			const wrapper = wrapperRef.current;
			const autocompletePopper = document.querySelector('.MuiAutocomplete-popper');
			const autocompleteRoot = document.querySelector('.MuiAutocomplete-root');
			const clearAllButton = document.querySelector('.clear-all-button');
			const clickedInsideWrapper = wrapper && typeof wrapper.contains === 'function' && wrapper.contains(event.target);

			if (
				!clickedInsideWrapper &&
				(!autocompletePopper || !autocompletePopper.contains(event.target)) &&
				(!clearAllButton || !clearAllButton.contains(event.target)) &&
				(!autocompleteRoot || !autocompleteRoot.contains(event.target)) &&
				event.target.textContent !== filterTitle
			) {
				setIsOpen(false);
			}
		}

		function handleEscape(event) {
			if (event.key === 'Escape') {
				setIsOpen(false);
			}
		}

		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleEscape);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleEscape);
		};
	}, []);

	return (
		<>
			<UpgradeModal isOpen={isUpgradeModalOpen} setIsOpen={setIsUpgradeModalOpen} message={upgradeModalMessage} />
			<div className="flex flex-col">
				{/* <Tooltip title={tooltip}>
					<div className="text-xl mb-3">{filterTitle}</div>
				</Tooltip> */}
				<Autocomplete
					disablePortal
					disableCloseOnSelect={!isSingleSelect}
					value={normalizedValue}
					options={displayOptions}
					groupBy={isSingleSelect ? (option) => option.sportGroup : undefined}
					renderGroup={isSingleSelect
						? (params) => (
								<li key={params.key} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
									<ListSubheader
										component="div"
										sx={{
											lineHeight: 1.5,
											padding: '8px 12px',
											fontSize: '0.75rem',
											fontWeight: 600,
											textTransform: 'uppercase',
											letterSpacing: '0.04em',
											color: 'var(--screener-text-muted, rgba(255,255,255,0.6))',
											backgroundColor: '#17191d',
											zIndex: 1,
											position: 'sticky',
											top: 0,
											margin: 0,
											borderRadius: 0,
										}}
									>
										{getSportDisplayName(params.group)}
									</ListSubheader>
									<ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>{params.children}</ul>
								</li>
						  )
						: undefined}
					getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label ?? '')}
					multiple={!isSingleSelect}
					isOptionEqualToValue={(option, val) => (val && option?.value === val?.value) || option?.value === val}
					open={isOpen}
					sx={autocompleteStyling}
					renderInput={(params) => {
						const { inputProps, InputProps } = params;
						const openAndSingle = isOpen && isSingleSelect;
						// Forward MUI's input ref only when the input mounts (callback ref). Never set to null at render.
						const setInputRef = (el) => {
							inputRef.current = el;
							if (inputProps?.ref) {
								if (typeof inputProps.ref === 'function') inputProps.ref(el);
								else inputProps.ref.current = el;
							}
						};
						return (
							<div
								ref={(el) => {
									wrapperRef.current = el;
									if (InputProps?.ref) {
										if (typeof InputProps.ref === 'function') InputProps.ref(el);
										else if (InputProps.ref) InputProps.ref.current = el;
									}
								}}
								style={{
									border: `1px solid ${isOpen ? 'var(--screener-accent, #3b82f6)' : 'var(--screener-border-strong, rgba(255,255,255,0.1))'}`,
									minHeight: openAndSingle ? '40px' : 52,
									padding: openAndSingle ? '6px 10px' : '8px 12px',
									borderRadius: 'var(--screener-radius-sm, 8px)',
									display: 'flex',
									flexWrap: 'wrap',
									alignItems: 'center',
									gap: 8,
									backgroundColor: isOpen ? 'rgba(59, 130, 246, 0.08)' : 'var(--screener-surface, #16181d)',
									outline: 'none',
								}}
								className={openAndSingle ? 'screener-autocomplete-open-single' : ''}
								onClick={() => {
									const copy = JSON.parse(JSON.stringify(whichFiltersOpen));
									copy[filterName] = !copy[filterName];
									setWhichFiltersOpen(copy);
									setIsOpen(!isOpen);
								}}>
								{/* League: one input always in DOM (hidden when closed) so MUI's ref is never null */}
								{isSingleSelect && !openAndSingle && (
									<input
										aria-hidden
										tabIndex={-1}
										type="text"
										{...inputProps}
										ref={setInputRef}
										style={{
											position: 'absolute',
											width: 0,
											height: 0,
											opacity: 0,
											pointerEvents: 'none',
											overflow: 'hidden',
											border: 'none',
											padding: 0,
											margin: 0,
										}}
										onKeyDown={(e) => e.key === 'Backspace' && e.stopPropagation()}
										placeholder={`Search for a ${filterPlaceholder}`}
									/>
								)}
								{/* When League open: single row = search + Clear. When closed or multi: title row + optional search row */}
								{openAndSingle ? (
									<>
										<div
											className="flex flex-row"
											style={{
												flex: 1,
												minWidth: 0,
												border: '1px solid var(--screener-border-strong)',
												borderRadius: 6,
												padding: '6px 8px 6px 32px',
												position: 'relative',
												alignItems: 'center',
											}}>
											<span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex' }}>
												<img src={magnifying_glass} alt="" style={{ opacity: 0.7 }} />
											</span>
											<input
												type="text"
												{...inputProps}
												ref={setInputRef}
												style={{
													border: 'none',
													outline: 'none',
													backgroundColor: 'transparent',
													width: '100%',
													fontSize: '13px',
													color: 'var(--screener-text, #f0f2f5)',
												}}
												onClick={(e) => e.stopPropagation()}
												onKeyDown={(e) => {
													if (e.key === 'Backspace' && e.currentTarget.value === '') e.stopPropagation();
												}}
												placeholder={`Search for a ${filterPlaceholder}`}
											/>
										</div>
									</>
								) : (
									<>
										<div
											className="flex flex-row"
											style={{
												width: '100%',
												minHeight: isSingleSelect && !filters?.[filterName] ? 36 : undefined,
												alignItems: 'center',
											}}>
											{((isSingleSelect && !filters?.[filterName]) || (!isSingleSelect && !filters?.[filterName]?.length)) && (
												<div
													className="cursor-pointer"
													style={{
														fontSize: filterTitle === 'Sportsbooks' && isOpen ? '10px' : 'inherit',
														color: 'var(--screener-text)',
														lineHeight: 1,
														padding: 4,
														paddingRight: 10,
														paddingLeft: 10,
														whiteSpace: 'nowrap',
													}}>
													{filterTitle}
												</div>
											)}

											{(isSingleSelect ? filters?.[filterName] : filters?.[filterName]?.length > 0) && (
												<div style={{ padding: 4, paddingRight: 10, paddingLeft: 10, cursor: 'pointer', color: 'var(--screener-text)', whiteSpace: 'nowrap' }}>{filters?.[filterName]}</div>
											)}

										{isOpen && !isSingleSelect && filters?.[filterName]?.length > 0 && (
											<div
												style={{ marginLeft: 'auto' }}
												onClick={async (e) => {
													e.stopPropagation();
													const copy = JSON.parse(JSON.stringify(filters));
													copy[filterName] = [];
													setFilters(copy);
												}}
												className="clear-all-button">
												<div size="small" className="clear-button" style={{ color: 'var(--screener-accent)', borderRadius: 5, padding: 4 }}>
													Clear
												</div>
											</div>
										)}
										</div>

										{isOpen && !isSingleSelect && (
											<div
												className="flex flex-row"
												style={{ maxWidth: '-webkit-fill-available', border: '1px solid var(--screener-border-strong)', borderRadius: 6, padding: 6 }}>
												<span>
													<img src={magnifying_glass} alt="magnifying glass" />
												</span>
												<input
													type="text"
													{...inputProps}
													style={{
														border: 'none',
														outline: 'none',
														backgroundColor: 'transparent',
														width: '130px',
														fontSize: '13px',
														color: 'var(--screener-text, #f0f2f5)',
													}}
													onClick={(e) => e.stopPropagation()}
													onKeyDown={(e) => {
														if (e.key === 'Backspace' && e.currentTarget.value === '') e.stopPropagation();
													}}
													placeholder={`Search for a ${filterPlaceholder}`}
													ref={setInputRef}
												/>
											</div>
										)}
									</>
								)}
							</div>
						);
					}}
					onChange={async (e, newValue) => {
						const limitFilters = ['sportsbooks'];
						const isLimitedFilter = limitFilters.includes(filterName);
						if (!isMasterOrOracle && isLimitedFilter && filterName === 'sportsbooks' && newValue?.length > 5) {
							newValue = newValue.slice(0, 5);
							setUpgradeModalMessage('Only 5 sportsbooks can be selected with your current plan');
							setIsUpgradeModalOpen(true);
						}

						let copy = {};
						if (filters) {
							copy = JSON.parse(JSON.stringify(filters));
						}
						// Single-select (e.g. League): newValue is one option or null
						copy[filterName] = isSingleSelect
							? (newValue?.value ?? null)
							: (Array.isArray(newValue) ? newValue.map((o) => o?.value).filter(Boolean) : newValue?.value);

						setFilters(copy);
						setRefreshCounter(refreshCounter + 1);
					}}
					renderOption={(props, option) => {
						const selected = isSingleSelect
							? (filters?.[filterName] === option.value || filters?.[filterName] === option.label)
							: (filters?.[filterName] === option.label || (Array.isArray(filters?.[filterName]) && filters?.[filterName]?.includes(option.label)));
						if (isSingleSelect) {
							return (
								<li
									{...props}
									className="screener-league-option"
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										color: 'var(--screener-text, #f0f2f5)',
										padding: '10px 12px',
										fontSize: '0.875rem',
										gap: 8,
									}}>
									<span>{option.label}</span>
									{selected && <Check sx={{ fontSize: 20, color: 'var(--screener-accent)' }} />}
								</li>
							);
						}
						return (
							<li {...props} style={{ display: 'flex', alignItems: 'center', color: 'white' }}>
								<img style={{ marginRight: '0.5rem' }} src={selected ? checked_checkbox : unchecked_checkbox} alt="checkbox" />
								<Typography style={{ fontWeight: selected ? 'bold' : '' }} variant="body1">
									{option.label}
								</Typography>
							</li>
						);
					}}
					slots={{
						paper: CustomPaper,
						popper: CustomPopper,
						noOptions: {
							sx: {
								color: 'white',
								padding: 1,
							},
						},
					}}
					slotProps={{
						popper: {
							modifiers: [],
						},
						paper: {
							sx: {
								...(isSingleSelect && {
									paddingTop: 0,
									'& .MuiAutocomplete-listbox': {
										paddingTop: 0,
										backgroundColor: '#17191d',
									},
								}),
								'& .MuiAutocomplete-option': {
									'&:hover, &[data-focus="true"], &:focus': {
										backgroundColor: '#4e5563 !important',
									},
								},
								'& .Mui-focusVisible': {
									backgroundColor: '#4e5563 !important',
								},
							},
						},
					}}
				/>
			</div>
		</>
	);
};
